import { Component } from '@theme/component';

/**
 * Waterfall navigation panel for desktop mega menus.
 * Two columns by default (primary → secondary). Optional tertiary column
 * when [data-waterfall-secondary] / [data-waterfall-tertiary] nodes exist.
 */
class WaterfallMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#hoverDelay = Number(this.dataset.hoverDelay) || 0;
    this.#primaryItems = Array.from(this.querySelectorAll('[data-waterfall-primary]'));
    this.#panels = Array.from(this.querySelectorAll('[data-waterfall-panel]'));
    this.#secondaryItems = Array.from(this.querySelectorAll('[data-waterfall-secondary]'));
    this.#tertiaryPanels = Array.from(this.querySelectorAll('[data-waterfall-tertiary]'));

    for (const item of this.#primaryItems) {
      item.addEventListener('mouseenter', this.#handlePrimaryMouseEnter);
      item.addEventListener('mouseleave', this.#handlePrimaryMouseLeave);
      item.addEventListener('focusin', this.#handlePrimaryFocus);
    }

    for (const item of this.#secondaryItems) {
      item.addEventListener('mouseenter', this.#handleSecondaryMouseEnter);
      item.addEventListener('mouseleave', this.#handleSecondaryMouseLeave);
      item.addEventListener('focusin', this.#handleSecondaryFocus);
    }

    this.addEventListener('mouseleave', this.#clearActive);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelPending();
    this.#cancelTertiaryPending();
  }

  /** @type {number} */
  #hoverDelay = 0;

  /** @type {HTMLElement[]} */
  #primaryItems = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {HTMLElement[]} */
  #secondaryItems = [];

  /** @type {HTMLElement[]} */
  #tertiaryPanels = [];

  /** @type {string | null} */
  #activeId = null;

  /** @type {string | null} */
  #activeTertiaryId = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #pendingTimer = null;

  /** @type {string | null} */
  #pendingPanelId = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #pendingTertiaryTimer = null;

  /** @type {string | null} */
  #pendingTertiaryId = null;

  /**
   * @param {MouseEvent} event
   */
  #handlePrimaryMouseEnter = (event) => {
    const item = event.currentTarget;

    if (!(item instanceof HTMLElement)) return;

    this.#activatePrimaryItem(item, { immediate: this.#hoverDelay === 0 });
  };

  /**
   * @param {FocusEvent} event
   */
  #handlePrimaryFocus = (event) => {
    const item = event.currentTarget;

    if (!(item instanceof HTMLElement)) return;

    this.#activatePrimaryItem(item, { immediate: true });
  };

  /**
   * @param {MouseEvent} event
   */
  #handlePrimaryMouseLeave = (event) => {
    const item = event.currentTarget;

    if (!(item instanceof HTMLElement)) return;

    const panelId = item.dataset.waterfallPrimary;

    if (panelId && panelId === this.#pendingPanelId) {
      this.#cancelPending();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleSecondaryMouseEnter = (event) => {
    const item = event.currentTarget;

    if (!(item instanceof HTMLElement)) return;

    this.#activateSecondaryItem(item, { immediate: this.#hoverDelay === 0 });
  };

  /**
   * @param {FocusEvent} event
   */
  #handleSecondaryFocus = (event) => {
    const item = event.currentTarget;

    if (!(item instanceof HTMLElement)) return;

    this.#activateSecondaryItem(item, { immediate: true });
  };

  /**
   * @param {MouseEvent} event
   */
  #handleSecondaryMouseLeave = (event) => {
    const item = event.currentTarget;

    if (!(item instanceof HTMLElement)) return;

    const panelId = item.dataset.waterfallSecondary;

    if (panelId && panelId === this.#pendingTertiaryId) {
      this.#cancelTertiaryPending();
    }
  };

  /**
   * @param {HTMLElement} item
   * @param {{ immediate?: boolean }} [options]
   */
  #activatePrimaryItem(item, { immediate = false } = {}) {
    const panelId = item.dataset.waterfallPrimary;

    if (!panelId) return;

    const hasChildren = item.classList.contains('waterfall-menu__primary-item--has-children');

    if (!hasChildren) {
      if (this.#activeId === null && !this.#pendingPanelId) {
        this.#setPrimaryActive(panelId);
        return;
      }

      if (immediate) {
        this.#cancelPending();
        this.#clearPanels();
        this.#setPrimaryActive(panelId);
        return;
      }

      this.#cancelPending();
      this.#pendingPanelId = panelId;
      this.#pendingTimer = setTimeout(() => {
        this.#clearPanels();
        this.#setPrimaryActive(panelId);
        this.#pendingTimer = null;
        this.#pendingPanelId = null;
      }, this.#hoverDelay);
      return;
    }

    if (this.#activeId === panelId) {
      this.#cancelPending();
      return;
    }

    if (immediate) {
      this.#cancelPending();
      this.#setActive(panelId);
      return;
    }

    this.#cancelPending();
    this.#pendingPanelId = panelId;
    this.#pendingTimer = setTimeout(() => {
      this.#setActive(panelId);
      this.#pendingTimer = null;
      this.#pendingPanelId = null;
    }, this.#hoverDelay);
  }

  /**
   * @param {HTMLElement} item
   * @param {{ immediate?: boolean }} [options]
   */
  #activateSecondaryItem(item, { immediate = false } = {}) {
    const panelId = item.dataset.waterfallSecondary;

    if (!panelId) return;

    const hasChildren = item.classList.contains('waterfall-menu__secondary-item--has-children');

    if (!hasChildren) {
      if (immediate) {
        this.#cancelTertiaryPending();
        this.#clearTertiary();
        this.#setSecondaryActive(panelId);
        return;
      }

      this.#cancelTertiaryPending();
      this.#pendingTertiaryId = panelId;
      this.#pendingTertiaryTimer = setTimeout(() => {
        this.#clearTertiary();
        this.#setSecondaryActive(panelId);
        this.#pendingTertiaryTimer = null;
        this.#pendingTertiaryId = null;
      }, this.#hoverDelay);
      return;
    }

    if (this.#activeTertiaryId === panelId) {
      this.#cancelTertiaryPending();
      return;
    }

    if (immediate) {
      this.#cancelTertiaryPending();
      this.#setTertiaryActive(panelId);
      return;
    }

    this.#cancelTertiaryPending();
    this.#pendingTertiaryId = panelId;
    this.#pendingTertiaryTimer = setTimeout(() => {
      this.#setTertiaryActive(panelId);
      this.#pendingTertiaryTimer = null;
      this.#pendingTertiaryId = null;
    }, this.#hoverDelay);
  }

  /**
   * @param {string} panelId
   */
  #setActive(panelId) {
    if (this.#activeId === panelId) return;

    this.#activeId = panelId;
    this.#setPrimaryActive(panelId);
    this.#clearTertiary();

    for (const panel of this.#panels) {
      const isActive = panel.dataset.waterfallPanel === panelId;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
    }
  }

  /**
   * @param {string} panelId
   */
  #setPrimaryActive(panelId) {
    for (const item of this.#primaryItems) {
      item.classList.toggle('is-active', item.dataset.waterfallPrimary === panelId);
    }
  }

  /**
   * @param {string} panelId
   */
  #setSecondaryActive(panelId) {
    for (const item of this.#secondaryItems) {
      item.classList.toggle('is-active', item.dataset.waterfallSecondary === panelId);
    }
  }

  /**
   * @param {string} panelId
   */
  #setTertiaryActive(panelId) {
    this.#activeTertiaryId = panelId;
    this.#setSecondaryActive(panelId);

    for (const panel of this.#tertiaryPanels) {
      const isActive = panel.dataset.waterfallTertiary === panelId;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
    }
  }

  #cancelPending() {
    if (this.#pendingTimer) {
      clearTimeout(this.#pendingTimer);
      this.#pendingTimer = null;
    }

    this.#pendingPanelId = null;
  }

  #cancelTertiaryPending() {
    if (this.#pendingTertiaryTimer) {
      clearTimeout(this.#pendingTertiaryTimer);
      this.#pendingTertiaryTimer = null;
    }

    this.#pendingTertiaryId = null;
  }

  #clearTertiary() {
    this.#cancelTertiaryPending();
    this.#activeTertiaryId = null;

    for (const panel of this.#tertiaryPanels) {
      panel.hidden = true;
      panel.classList.remove('is-active');
    }

    for (const item of this.#secondaryItems) {
      item.classList.remove('is-active');
    }
  }

  #clearPanels = () => {
    this.#cancelPending();
    this.#activeId = null;
    this.#clearTertiary();

    for (const panel of this.#panels) {
      panel.hidden = true;
      panel.classList.remove('is-active');
    }

    for (const item of this.#primaryItems) {
      item.classList.remove('is-active');
    }
  };

  #clearActive = () => {
    this.#clearPanels();
  };
}

if (!customElements.get('waterfall-menu')) {
  customElements.define('waterfall-menu', WaterfallMenu);
}
